import { useEffect, useRef, useState } from "react";
import { CustomerPinCard } from "../components/CustomerPinCard";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "../lib/api";
import { WalletPass } from "../components/WalletPass";
import { BrandMark } from "../components/BrandMark";
import { Check, ShieldCheck, Smartphone, ScanLine, Wallet } from "lucide-react";
import QRCode from "../components/QRCode";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const isAndroid = () => typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

// Official-style wallet buttons: Apple = black, Google = black pill with the Wallet mark (brand guidelines).
function AppleButton({ href, onClick, disabled, testid }) {
  const cls = "btn-lift flex items-center justify-center gap-2 w-full bg-[#0B0D10] text-white rounded-2xl h-14 font-semibold text-[15px] hover:bg-black disabled:opacity-60";
  if (href) return <a href={href} data-testid={testid} className={cls}><Smartphone size={18} />Add to Apple Wallet</a>;
  return <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} className={cls}><Smartphone size={18} />Add to Apple Wallet</button>;
}

function GoogleButton({ href, onClick, disabled, live = true, testid }) {
  const cls = "btn-lift flex items-center justify-center gap-2 w-full bg-[#1F1F1F] text-white rounded-2xl h-14 font-semibold text-[15px] hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed";
  const label = live ? "Add to Google Wallet" : "Google Wallet · coming soon";
  if (href && live) return <a href={href} data-testid={testid} className={cls}><Wallet size={18} />{label}</a>;
  return <button type="button" onClick={onClick} disabled={disabled || !live} data-testid={testid} className={cls}><Wallet size={18} />{label}</button>;
}

export default function PublicJoin() {
  const { orgSlug, programSlug } = useParams();
  const [params] = useSearchParams();
  const src = params.get("src");
  const [prog, setProg] = useState(null);
  const [err, setErr] = useState(null);
  const [joined, setJoined] = useState(null);
  const [busy, setBusy] = useState(false);
  const scanFired = useRef(false);
  const joining = useRef(false);

  useEffect(() => {
    api.get(`/public/programs/${orgSlug}/${programSlug}`).then((r) => setProg(r.data)).catch((e) => setErr(apiError(e)));
  }, [orgSlug, programSlug]);

  useEffect(() => {
    // Guard against StrictMode double-invocation of effects — one scan per page load
    if (!src || scanFired.current) return;
    const key = `bp-scan-${orgSlug}-${programSlug}-${src}`;
    if (typeof window !== "undefined" && sessionStorage.getItem(key)) { scanFired.current = true; return; }
    scanFired.current = true;
    if (typeof window !== "undefined") sessionStorage.setItem(key, "1");
    api.post("/telemetry/marketing/scan", { src, org_slug: orgSlug, program_slug: programSlug }).catch(() => {});
  }, [src, orgSlug, programSlug]);

  const join = async (wallet_type) => {
    if (joining.current || busy) return;
    joining.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/programs/${orgSlug}/${programSlug}/join`, { wallet_type, src });
      await api.post(`/public/passes/${data.barcode_token}/install`);
      setJoined(data);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); joining.current = false; }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC] p-6 text-center"><div><BrandMark size={40} /><p className="mt-4 text-[#25282E]">{err}</p></div></div>;
  if (!prog) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><div className="animate-pulse font-display font-bold">Loading…</div></div>;

  const wallets = prog.wallets || {};
  const googleLive = !!wallets.google;
  const android = isAndroid();
  const installApple = joined ? `${BACKEND}${joined.wallet_install?.install_url_apple}` : null;
  const installGoogle = joined ? `${BACKEND}${joined.wallet_install?.install_url_google}` : null;
  const chosenGoogle = joined?.wallet_type === "google";
  const joinButtons = [
    <AppleButton key="apple" testid="add-apple-wallet" disabled={busy} onClick={() => join("apple")} />,
    <GoogleButton key="google" testid="add-google-wallet" disabled={busy} live={googleLive} onClick={() => join("google")} />,
  ];

  return (
    <div className="min-h-screen bg-[#F4F1E9] flex flex-col items-center px-5 py-10">
      <div className="flex items-center gap-2 mb-8"><BrandMark size={26} /><span className="font-display font-bold">BackPocket</span></div>
      <div className="w-full max-w-md">
        {!joined ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <h1 className="font-display text-3xl font-bold tracking-tight">Keep your rewards where you keep everything else.</h1>
            <p className="text-[#25282E] mt-3">{prog.description || `Join ${prog.org_name}'s loyalty program.`}</p>
            <div className="flex justify-center my-8"><WalletPass program={prog} state={{ balance: 0 }} /></div>
            <div className="rounded-2xl bg-white border border-[#E8E5DE] p-4 text-left mb-6">
              <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck size={16} className="text-[#6474FF]" />No app, account or paper card required.</div>
            </div>
            <div className="space-y-3" data-testid="join-wallet-buttons">
              {android ? [...joinButtons].reverse() : joinButtons}
            </div>
            <p className="text-xs text-[#25282E]/60 mt-6">By adding this pass you agree to the business's terms. {prog.terms}</p>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center" data-testid="join-success">
            <div className="h-16 w-16 rounded-full bg-[#C8FF4D] flex items-center justify-center mx-auto mb-6"><Check size={32} className="text-[#0B0D10]" /></div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{joined.existing ? "Welcome back!" : "You're in!"}</h1>
            <p className="text-[#25282E] mt-2" data-testid="join-success-copy">
              {joined.existing
                ? <>This phone already has pass <strong>{joined.pass_number}</strong>. Add it to your wallet again below if it went missing.</>
                : <>Your pass <strong>{joined.pass_number}</strong> is ready. Show it next time you visit.</>}
            </p>
            <div className="flex justify-center my-8"><WalletPass program={prog} state={{ balance: joined.balance || 0, pass_number: joined.pass_number }} /></div>
            {prog.redeem_pin_required && <CustomerPinCard token={joined.barcode_token} pinSet={false} light />}

            {chosenGoogle && joined.wallet_install?.google_live && (
              <div className="mb-3"><GoogleButton href={installGoogle} testid="join-google-wallet-live" /></div>
            )}
            {!chosenGoogle && joined.wallet_install?.apple_live && (
              <div className="mb-3"><AppleButton href={installApple} testid="join-apple-wallet-live" /></div>
            )}
            {chosenGoogle && joined.wallet_install?.apple_live && (
              <a href={installApple} data-testid="join-apple-wallet-alt" className="block text-[12.5px] text-[#25282E]/70 underline underline-offset-2 mb-5">On an iPhone? Add to Apple Wallet instead</a>
            )}
            {!chosenGoogle && joined.wallet_install?.google_live && (
              <a href={installGoogle} data-testid="join-google-wallet-alt" className="block text-[12.5px] text-[#25282E]/70 underline underline-offset-2 mb-5">On Android? Add to Google Wallet instead</a>
            )}

            {/* Real scannable QR — staff can scan this off the customer's phone with the BackPocket Scanner. */}
            <div className="rounded-3xl bg-[#0B0D10] text-white p-6 grain relative overflow-hidden" data-testid="join-show-code">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#C8FF4D]/10 blur-3xl" />
              <div className="relative z-10 flex flex-col items-center">
                <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] text-[#C8FF4D] mb-4"><ScanLine size={13} />Show this at the counter</div>
                <div className="bg-white p-3 rounded-2xl">
                  <QRCode value={`bp:${joined.barcode_token}`} size={196} />
                </div>
                <div className="mt-4 font-mono text-[12px] tracking-[0.16em] text-white/70">{joined.pass_number}</div>
                <div className="mt-1 text-[11.5px] text-white/50 max-w-[220px]">{(joined.wallet_install?.apple_live || joined.wallet_install?.google_live) ? "Or scan this QR at the counter." : "Your barista scans it to add a stamp."}</div>
              </div>
            </div>

            {!joined.wallet_install?.apple_live && !joined.wallet_install?.google_live && (
              <div className="mt-5 rounded-2xl bg-white border border-[#E8E5DE] p-4 text-sm text-[#25282E]">
                A native wallet install lands here once live PassKit credentials are connected. Until then, the QR above works with any BackPocket scanner.
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
